import {
  Alert,
  Collapse,
  Dialog,
  FormControl,
  Grid,
  TextField,
  Fade,
  DialogTitle,
  IconButton,
  DialogContent,
  CardContent,
  Typography,
  DialogActions,
  Link,
} from '@mui/material';
import React from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import { get, replace, size, slice, split } from 'lodash';
import { Close } from '@mui/icons-material';
import axios from 'axios';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { CreateGuestJobValueType } from './page';
import FormActions from './form_actions';
import { GB_PRIME_PAY_CARD_TOKEN_URL, GB_PRIME_PAY_PUBLIC_KEY } from '../../config';
import { createScopedI18n } from '../../i18n/i18n';

const pageI18n = createScopedI18n('pages.fast_track.payment_form_dialog', { joinOutput: true });
const fastTrackI18n = createScopedI18n('pages.fast_track', { joinOutput: true });

const PaymentSchema = Yup.object({
  // payment
  cardNumber: Yup.string()
    .required(fastTrackI18n('payment_form_dialog.fields.card_number.blank'))
    .min(12, fastTrackI18n('payment_form_dialog.fields.card_number.invalid'))
    .max(19, fastTrackI18n('payment_form_dialog.fields.card_number.invalid')),
  expirationDate: Yup.string()
    .required(fastTrackI18n('payment_form_dialog.fields.expiration_date.blank'))
    .matches(/0[1-9]|1[0-2]\/[0-9]{2}/, fastTrackI18n('payment_form_dialog.fields.expiration_date.invalid')),
  cvc: Yup.string()
    .required(fastTrackI18n('payment_form_dialog.fields.cvc.blank'))
    .min(3, fastTrackI18n('payment_form_dialog.fields.cvc.invalid'))
    .max(4, fastTrackI18n('payment_form_dialog.fields.cvc.invalid')),
});

interface PaymentFormDialogProps {
  form: UseFormReturn<CreateGuestJobValueType>;
  amount?: number | null | undefined;
  open: boolean;
  loading?: boolean;
  onClose: (reason: 'close' | 'submit') => void;
}

const formatExpirationDate = (expirationDate: string | null | undefined) => {
  if (expirationDate && size(expirationDate) > 2) {
    const expirationDateTrim = replace(expirationDate, '/', '').trim();
    return [slice(expirationDateTrim, 0, 2).join(''), slice(expirationDateTrim, 2).join('')].join('/');
  }
  return expirationDate;
};

const PaymentFormDialog: React.FC<PaymentFormDialogProps> = ({ form, open, loading, onClose, amount = 0 }) => {
  const { setValue: setFastTrackValue } = form;
  const {
    control: paymentControl,
    trigger: paymentTrigger,
    getValues: getPaymentValues,
    reset: paymentReset,
  } = useForm({ resolver: yupResolver(PaymentSchema) });

  const [errorMessage, setErrorMessage] = React.useState<string | null>();

  const handleSubmit = async () => {
    const isValid = await paymentTrigger(['cardNumber', 'expirationDate', 'cvc']);
    if (!isValid) {
      setErrorMessage('กรุณากรอกข้อมูลให้ถูกต้อง');
      return;
    }

    if ((amount ?? 0) <= 0) return;

    setErrorMessage(null);
    const [expirationMonth, expirationYear] = split(getPaymentValues('expirationDate'), '/');

    try {
      const gbPrimeResponse = await axios.post(
        GB_PRIME_PAY_CARD_TOKEN_URL,
        {
          rememberCard: false,
          card: {
            number: getPaymentValues('cardNumber'),
            expirationMonth,
            expirationYear,
            securityCode: getPaymentValues('cvc'),
          },
        },
        {
          headers: {
            'Authorization': `Basic ${btoa(`${GB_PRIME_PAY_PUBLIC_KEY}:`)}`, //! FIXME: Use node Buffer for string -> Base64
            'Content-Type': 'application/json',
          },
        },
      );

      if (get(gbPrimeResponse.data, 'resultCode') === '00') {
        setFastTrackValue('token', get(gbPrimeResponse.data, ['card', 'token']));
        onClose('submit');
      } else {
        setErrorMessage(pageI18n('error.card_can_not_usage'));
      }
    } catch (error) {
      setErrorMessage(get(error, 'message'));
    }
  };

  const handleClose = () => {
    setErrorMessage(null);
    paymentReset(
      { ...getPaymentValues(), cardNumber: undefined, expirationDate: undefined, cvc: undefined },
      {
        keepValues: true,
        keepDefaultValues: true,
        keepTouched: true,
        keepIsValid: true,
        keepDirty: true,
        keepDirtyValues: true,
      },
    );
    onClose('close');
  };

  return (
    <Dialog fullWidth maxWidth="sm" TransitionComponent={Fade} onClose={handleClose} open={open}>
      <DialogTitle display="inline-flex" flexDirection="row" justifyContent="space-between" alignItems="center">
        {pageI18n('title')}
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Collapse in={!!errorMessage}>
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {errorMessage}
        </Alert>
      </Collapse>

      <DialogContent dividers>
        <CardContent sx={{ paddingX: 0 }}>
          <Grid container gap={2}>
            <Controller
              control={paymentControl}
              name="cardNumber"
              render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
                <FormControl id={field.name} fullWidth error={invalid}>
                  <TextField
                    id={field.name}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    fullWidth
                    label={`${pageI18n('fields.card_number.label')} *`}
                    value={value ?? ''}
                    placeholder="•••• •••• •••• ••••"
                    error={invalid}
                    helperText={error?.message}
                    inputProps={{ pattern: '[0-9]{12,19}' }}
                    onChange={(e) => {
                      // Remove all whitespace characters from the input value
                      const sanitizedValue = e.target.value.replace(/\s/g, '');
                      onChange(sanitizedValue); // Update the form with the sanitized value
                    }}
                    {...field}
                  />
                </FormControl>
              )}
            />

            <Grid item xs={7}>
              <Controller
                control={paymentControl}
                name="expirationDate"
                render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                  <FormControl id={field.name} fullWidth error={invalid}>
                    <TextField
                      id={field.name}
                      type="tel"
                      inputMode="numeric"
                      autoComplete="cc-exp"
                      fullWidth
                      label={`${pageI18n('fields.expiration_date.label')} *`}
                      value={formatExpirationDate(value) ?? ''}
                      placeholder="MM/YY"
                      error={invalid}
                      helperText={error?.message}
                      inputProps={{ pattern: '(0[1-9]|1[0-2])/[0-9]{2}' }}
                      {...field}
                    />
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs>
              <Controller
                control={paymentControl}
                name="cvc"
                render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                  <FormControl id={field.name} fullWidth error={invalid}>
                    <TextField
                      id={field.name}
                      type="password"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      fullWidth
                      label={`${pageI18n('fields.cvc.label')} *`}
                      value={value ?? ''}
                      placeholder="•••"
                      error={invalid}
                      helperText={error?.message}
                      inputProps={{ pattern: '[0-9]{3,4}' }}
                      {...field}
                    />
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </DialogContent>

      <DialogActions
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          paddingX: 3,
          paddingBottom: 3,
        }}
      >
        <Typography variant="overline">
          {`${pageI18n('footer.agreement')} `}
          <Link href="https://www.findtemp.in.th/fast-track-refund-policy" target="_blank" rel="noopener">
            {`${pageI18n('footer.refund_policy')}`}
          </Link>
        </Typography>
        <FormActions nextButtonText={pageI18n('confirm_button')} loading={loading} onNext={handleSubmit} />
      </DialogActions>
    </Dialog>
  );
};

export default PaymentFormDialog;
